import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { Nav, NavItem, NavLink as RsNavLink, Offcanvas, OffcanvasBody, Spinner } from 'reactstrap';
import PerfectScrollbar from 'react-perfect-scrollbar';
import 'react-perfect-scrollbar/dist/css/styles.css';
import {
    fetchInfluencerSocialAccount,
    fetchSocialAccountApplicationsThunk,
    fetchSocialAccountCollaborationsThunk,
} from 'slices/influencer/thunk';
import { AppDispatch, RootState } from 'store';
import { genders } from 'constants/campaign';
import SocialAccountProfile from 'Components/campaigns/applications/social-account/SocialAccountProfile';
import SocialAccountStats from 'Components/campaigns/applications/social-account/SocialAccountStats';
import SocialAccountInsights from 'Components/campaigns/applications/social-account/SocialAccountInsights';
import ApplicationViewMedia from 'Components/campaigns/applications/social-account/ApplicationViewMedia';
import CloseButton from 'Components/Common/CloseButton';
import { SocialAccountCollaborationItem } from 'types/social-account';
import { toast } from 'react-toastify';
import { ERROR } from 'types';
import 'Components/campaigns/applications/style.scss';
import './SocialAccountView.scss';
import { onboardCreator } from 'services/api/influencer';
import { copyToClipboard, getErrorMessage } from 'utils/helpers';
import { toggleSocialAccountOnboarding } from 'slices/ui/reducer';

type HistoryTab = 'applications' | 'collaborations';

interface SocialAccountViewProps {
    isOpen: boolean;
    toggleOffcanvas: () => void;
    socialAccountId: string;
    influencerId?: string;
}

const statusClass = (status?: string) => {
    const normalized = (status ?? '').toLowerCase();
    if (normalized === 'accepted' || normalized === 'approved' || normalized === 'completed') {
        return 'social-account-view__status--success';
    }
    if (normalized === 'rejected' || normalized === 'cancelled') {
        return 'social-account-view__status--danger';
    }
    if (normalized === 'pending' || normalized === 'processing') {
        return 'social-account-view__status--warning';
    }
    return 'social-account-view__status--muted';
};

const SocialAccountView = ({ isOpen, toggleOffcanvas, socialAccountId, influencerId }: SocialAccountViewProps) => {
    const dispatch: AppDispatch = useDispatch();
    const [activeTab, setActiveTab] = useState<HistoryTab>('applications');
    const [onboarding, setOnboarding] = useState(false);
    const [onboardingData, setOnboardingData] = useState<{ message: string, handle: string, profileUrl: string } | null>(null);

    const socialAccount = useSelector(
        (state: RootState) => state.influencers.social_accounts[socialAccountId]?.social_account
    );
    const applicationsCache = useSelector(
        (state: RootState) => state.influencers.social_account_applications[socialAccountId]
    );
    const collaborationsCache = useSelector(
        (state: RootState) => state.influencers.social_account_collaborations[socialAccountId]
    );
    const nicheList = useSelector((state: RootState) => state.auth.master?.niche_list ?? {});
    const specialityNiches = useSelector((state: RootState) => state.auth.master?.speciality_niches) ?? [];

    const applications = applicationsCache?.items ?? [];
    const collaborations = collaborationsCache?.items ?? [];
    const loadingApplications = Boolean(applicationsCache?.loading && applications.length === 0);
    const loadingCollaborations = Boolean(collaborationsCache?.loading && collaborations.length === 0);
    const platform = socialAccount?.platform;
    const showOnboardCta = Boolean(socialAccount) && platform === 'ig' && !influencerId;

    useEffect(() => {
        if (isOpen && socialAccountId) {
            dispatch(fetchInfluencerSocialAccount({ socialAccountId }));
        }
    }, [dispatch, isOpen, socialAccountId]);

    useEffect(() => {
        if (!isOpen) {
            setActiveTab('applications');
            setOnboarding(false);
            setOnboardingData(null);
        }
    }, [isOpen]);

    useEffect(() => {
        setOnboardingData(null);
        setOnboarding(false);
    }, [socialAccountId]);

    useEffect(() => {
        if (!isOpen || !socialAccountId) return;
        if (activeTab === 'applications') {
            dispatch(fetchSocialAccountApplicationsThunk({ socialAccountId })).then((result) => {
                if (fetchSocialAccountApplicationsThunk.rejected.match(result) && result.payload && result.payload !== ERROR.CACHED) {
                    toast.error(result.payload);
                }
            });
        } else {
            dispatch(fetchSocialAccountCollaborationsThunk({ socialAccountId })).then((result) => {
                if (fetchSocialAccountCollaborationsThunk.rejected.match(result) && result.payload && result.payload !== ERROR.CACHED) {
                    toast.error(result.payload);
                }
            });
        }
    }, [activeTab, dispatch, isOpen, socialAccountId]);

    const creatorGender = useMemo(() => {
        if (platform === 'ig' && socialAccount?.ig?.gender) {
            return genders[socialAccount.ig.gender as keyof typeof genders];
        }
        return '';
    }, [platform, socialAccount]);

    const creatorLocation = useMemo(() => socialAccount?.location ?? '', [socialAccount]);

    const creatorAge = useMemo(() => {
        if (platform === 'ig' && socialAccount?.ig?.age_bucket) {
            return socialAccount.ig.age_bucket ?? '';
        }
        return '';
    }, [platform, socialAccount]);

    const followersCount = useMemo(() => {
        if (platform === 'ig') return socialAccount?.ig?.followers_count ?? socialAccount?.followers_count ?? 0;
        if (platform === 'yt') return socialAccount?.yt?.subscriber_count ?? socialAccount?.followers_count ?? 0;
        return socialAccount?.followers_count ?? 0;
    }, [platform, socialAccount]);

    const specialityNichesTier2 = useMemo(() => {
        if (!Array.isArray(specialityNiches)) return [];
        const tier2 = [];
        for (const niche of specialityNiches) {
            if (Array.isArray(niche.children)) tier2.push(...niche.children);
        }
        return tier2;
    }, [specialityNiches]);

    const creatorNiches = useMemo(() => {
        if (socialAccount?.speciality_niches_tier2 && socialAccount.speciality_niches_tier2.length > 0) {
            return socialAccount.speciality_niches_tier2
                .map((niche) => (specialityNichesTier2 ?? []).find((n) => Number(n.id) === Number(niche))?.name ?? '')
                .filter((label): label is string => Boolean(label));
        }
        if (socialAccount?.speciality_niches && socialAccount.speciality_niches.length > 0) {
            return socialAccount.speciality_niches
                .map((niche) => (specialityNiches ?? []).find((n) => Number(n.id) === Number(niche))?.name ?? '')
                .filter((label): label is string => Boolean(label));
        }
        if (socialAccount?.niches && socialAccount.niches.length > 0) {
            return socialAccount.niches
                .map((nicheId) => nicheList[nicheId])
                .filter((label): label is string => Boolean(label));
        }
        return [];
    }, [nicheList, socialAccount, specialityNiches, specialityNichesTier2]);

    const hasDemographicInsights = useMemo(() => {
        const demographic =
            platform === 'ig'
                ? socialAccount?.ig?.insights?.demographic
                : socialAccount?.yt?.insights?.demographic;
        if (!demographic) return false;

        return (
            Object.keys(demographic.gender ?? {}).length > 0 ||
            Object.keys(demographic.age ?? {}).length > 0 ||
            Object.keys(demographic.city ?? {}).length > 0 ||
            Object.keys(demographic.country ?? {}).length > 0
        );
    }, [platform, socialAccount]);

    const collaborationUrl = (item: SocialAccountCollaborationItem) => {
        const milestoneId = item.active_milestone?.id;
        if (milestoneId) {
            return `/tasks/${item.campaign.uuid}/collaboration/${item.uuid}/milestone/${milestoneId}`;
        }
        return `/tasks/${item.campaign.uuid}/collaboration/${item.uuid}`;
    };

    const showOnboardingData = async (onboardingPayload: { message: string, handle: string, profileUrl: string }) => {
        if (onboardingPayload.message) {
            await copyToClipboard(onboardingPayload.message, 'Message copied to clipboard');
        }
        dispatch(toggleSocialAccountOnboarding(onboardingPayload));
    };

    const handleOnboard = async () => {
        const handle = socialAccount?.handle;
        if (!socialAccountId || onboarding) return;
        if (onboardingData) {
            return showOnboardingData(onboardingData);
        }
        setOnboarding(true);
        try {
            const message = await onboardCreator(socialAccountId, 'onboarding');
            const payload = {
                message: message || '',
                handle: handle || '',
                profileUrl: handle ? `https://ig.me/m/${handle}` : '#',
            };
            setOnboardingData(payload);
            await showOnboardingData(payload);
        } catch (err) {
            toast.error(getErrorMessage(err, 'Failed to onboard creator'));
        } finally {
            setOnboarding(false);
        }
    };

    return (
        <Offcanvas
            isOpen={isOpen}
            toggle={toggleOffcanvas}
            direction="end"
            id="social-account-view-offcanvas"
            className="application-view-offcanvas application-view-offcanvas--merged social-account-view-offcanvas"
        >
            <CloseButton onClose={toggleOffcanvas} />
            <OffcanvasBody className="application-view-body vh-100 overflow-hidden">
                <div className="application-view">
                    <div className="application-view__panel application-view__panel--with-footer">
                        <div className="application-view__panel-scroll">
                            <PerfectScrollbar options={{ suppressScrollX: true }}>
                                <div className="application-view__panel-content">
                                    {socialAccount ? (
                                        <>
                                            <SocialAccountProfile
                                                socialAccount={socialAccount}
                                                name={socialAccount.name ?? ''}
                                                handle={socialAccount.handle!}
                                                followersCount={followersCount}
                                                gender={creatorGender}
                                                location={creatorLocation}
                                                age={creatorAge}
                                                niches={creatorNiches}
                                            />
                                            <SocialAccountStats socialAccount={socialAccount} />
                                            {hasDemographicInsights ? (
                                                <div className="application-view__merged-panel mt-3">
                                                    <SocialAccountInsights socialAccount={socialAccount} />
                                                </div>
                                            ) : null}

                                            <div className="social-account-view__history mt-3">
                                                <Nav tabs className="social-account-view__tabs nav-tabs-custom">
                                                    <NavItem>
                                                        <RsNavLink
                                                            className={activeTab === 'applications' ? 'active' : ''}
                                                            href="#"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                setActiveTab('applications');
                                                            }}
                                                        >
                                                            Applications
                                                        </RsNavLink>
                                                    </NavItem>
                                                    <NavItem>
                                                        <RsNavLink
                                                            className={activeTab === 'collaborations' ? 'active' : ''}
                                                            href="#"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                setActiveTab('collaborations');
                                                            }}
                                                        >
                                                            Collaborations
                                                        </RsNavLink>
                                                    </NavItem>
                                                </Nav>

                                                <div className="social-account-view__list">
                                                    {activeTab === 'applications' && (
                                                        <>
                                                            {loadingApplications ? (
                                                                <div className="social-account-view__empty">
                                                                    <Spinner size="sm" /> Loading applications...
                                                                </div>
                                                            ) : applications.length === 0 ? (
                                                                <div className="social-account-view__empty">
                                                                    No applications found
                                                                </div>
                                                            ) : (
                                                                applications.map((item) => (
                                                                    <NavLink
                                                                        key={item.uuid}
                                                                        to={`/campaign/${item.campaign.uuid}/applications`}
                                                                        className="social-account-view__row"
                                                                        onClick={toggleOffcanvas}
                                                                    >
                                                                        <div className="social-account-view__row-main">
                                                                            <span className="social-account-view__row-title">
                                                                                {item.campaign?.name ?? 'Campaign'}
                                                                            </span>
                                                                            <span
                                                                                className={`social-account-view__status ${statusClass(item.status)}`}
                                                                            >
                                                                                {item.status}
                                                                            </span>
                                                                        </div>
                                                                        <i className="bx bx-chevron-right social-account-view__row-chevron" />
                                                                    </NavLink>
                                                                ))
                                                            )}
                                                        </>
                                                    )}

                                                    {activeTab === 'collaborations' && (
                                                        <>
                                                            {loadingCollaborations ? (
                                                                <div className="social-account-view__empty">
                                                                    <Spinner size="sm" /> Loading collaborations...
                                                                </div>
                                                            ) : collaborations.length === 0 ? (
                                                                <div className="social-account-view__empty">
                                                                    No collaborations found
                                                                </div>
                                                            ) : (
                                                                collaborations.map((item) => (
                                                                    <NavLink
                                                                        key={item.uuid}
                                                                        to={collaborationUrl(item)}
                                                                        className="social-account-view__row"
                                                                        onClick={toggleOffcanvas}
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                    >
                                                                        <div className="social-account-view__row-main">
                                                                            <span className="social-account-view__row-title">
                                                                                {item.campaign?.name ?? 'Campaign'}
                                                                            </span>
                                                                            <span
                                                                                className={`social-account-view__status ${statusClass(item.status)}`}
                                                                            >
                                                                                {item.status}
                                                                            </span>
                                                                        </div>
                                                                        <i className="bx bx-chevron-right social-account-view__row-chevron" />
                                                                    </NavLink>
                                                                ))
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="social-account-view__empty py-5">
                                            <Spinner size="sm" /> Loading account...
                                        </div>
                                    )}
                                </div>
                            </PerfectScrollbar>
                        </div>

                        {showOnboardCta ? (
                            <div className="application-view__panel-footer">
                                <div className="application-view__panel-footer-inner">
                                    <button
                                        type="button"
                                        className="application-view__footer-btn application-view__footer-btn--primary"
                                        style={{ gridColumn: '1 / -1', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}
                                        disabled={onboarding}
                                        onClick={handleOnboard}
                                    >
                                        {onboarding ? (
                                            <Spinner size="sm" color="light" />
                                        ) : (
                                            <>
                                                <i className="ri-user-add-line" />
                                                {onboardingData ? 'View Onboard Message' : 'Onboard Creator'}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        ) : null}
                    </div>

                    <div className="application-view__panel pb-3">
                        <PerfectScrollbar options={{ suppressScrollX: true }}>
                            <div className="application-view__panel-content">
                                {socialAccount && socialAccountId ? (
                                    <ApplicationViewMedia
                                        socialAccountId={socialAccountId}
                                        socialAccount={socialAccount}
                                    />
                                ) : null}
                            </div>
                        </PerfectScrollbar>
                    </div>
                </div>
            </OffcanvasBody>
        </Offcanvas>
    );
};

export default SocialAccountView;
